import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, LayoutGrid, ShoppingCart, Heart, User, Bell, ScanBarcode, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useState, useEffect, createContext, useContext } from "react";

export const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

export default function StoreLayout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kiyana_cart") || "[]"); } catch { return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kiyana_wish") || "[]"); } catch { return []; }
  });

  useEffect(() => { localStorage.setItem("kiyana_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("kiyana_wish", JSON.stringify(wishlist)); }, [wishlist]);
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (user) api.get("/notifications").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  }, [user]);
  const [chatUnread, setChatUnread] = useState(0);
  useEffect(() => {
    if (!user || user.role !== "pelanggan") return;
    const f = () => api.get("/chat/unread").then((r) => setChatUnread(r.data.unread || 0)).catch(() => {});
    f();
    const t = setInterval(f, 20000);
    return () => clearInterval(t);
  }, [user]);

  const addToCart = (product, qty = 1, variant = null) => {
    setCart((c) => {
      const key = product.id + (variant || "");
      const existing = c.find((x) => x.key === key);
      if (existing) return c.map((x) => x.key === key ? { ...x, qty: x.qty + qty } : x);
      return [...c, { key, id: product.id, name: product.name, price: product.price, image: product.image, variant, qty }];
    });
  };
  const removeFromCart = (key) => setCart((c) => c.filter((x) => x.key !== key));
  const updateQty = (key, qty) => setCart((c) => c.map((x) => x.key === key ? { ...x, qty: Math.max(1, qty) } : x));
  const updateVariant = (key, variant) => setCart((c) => c.map((x) => x.key === key ? { ...x, variant, key: x.id + variant } : x));
  const toggleWish = (product) => setWishlist((w) => {
    if (w.find((x) => x.id === product.id)) return w.filter((x) => x.id !== product.id);
    return [...w, { id: product.id, name: product.name, price: product.price, image: product.image }];
  });

  const items = [
    { to: "/store", icon: Home, label: "Beranda", end: true, testid: "store-nav-home" },
    { to: "/store/categories", icon: LayoutGrid, label: "Kategori", testid: "store-nav-cat" },
    { to: "/store/cart", icon: ShoppingCart, label: "Keranjang", testid: "store-nav-cart", badge: cart.length },
    { to: "/store/wishlist", icon: Heart, label: "Wishlist", testid: "store-nav-wish" },
    { to: "/store/account", icon: User, label: "Akun", testid: "store-nav-account" },
  ];

  return (
    <CartCtx.Provider value={{ cart, wishlist, addToCart, removeFromCart, updateQty, updateVariant, toggleWish, setCart }}>
      <div className="min-h-screen bg-canvas-soft pb-24 text-cocoa">
        <header className="sticky top-0 z-30 bg-canvas-soft/95 backdrop-blur border-b border-blush-soft">
          <div className="max-w-2xl mx-auto px-4 pt-3 pb-2">
            <div className="flex items-center justify-between">
              <div className="w-8" />
              <button onClick={() => nav("/store")} className="text-center">
                <div className="font-serif-fashion text-xl tracking-[0.25em] text-cocoa">KIYANA</div>
                <div className="text-[9px] tracking-[0.2em] text-cocoa-muted">by Cirebon Cloth</div>
              </button>
              <div className="flex items-center gap-1">
                {user && user.role !== "pelanggan" && (
                  <button data-testid="store-back-portal-btn" onClick={() => nav("/")} className="text-[10px] px-2 py-1 rounded-full bg-blush text-brand font-medium">Portal</button>
                )}
                <button data-testid="store-bell-btn" onClick={() => nav("/store/notifications")} className="relative w-9 h-9 rounded-full flex items-center justify-center text-cocoa">
                  <Bell className="w-5 h-5" />
                  {unread > 0 && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-brand rounded-full border-2 border-canvas-soft" />}
                </button>
              </div>
            </div>
            <div className="mt-2 relative">
              <input
                data-testid="store-search-input"
                placeholder="Cari produk lucu di Kiyana..."
                className="w-full h-10 pl-4 pr-11 rounded-full border border-blush-soft bg-white focus:outline-none focus:ring-2 ring-brand text-sm"
                onKeyDown={(e) => e.key === "Enter" && nav(`/store/categories?q=${encodeURIComponent(e.target.value)}`)}
              />
              <ScanBarcode className="w-5 h-5 absolute right-3.5 top-1/2 -translate-y-1/2 text-cocoa-muted" />
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-4">
          <Outlet />
        </main>

        {(!user || user.role === "pelanggan") && (
          <button data-testid="store-chat-fab" onClick={() => nav("/store/chat")} className="fixed bottom-20 right-4 z-40 w-12 h-12 rounded-full bg-brand text-white shadow-lg shadow-brand/30 flex items-center justify-center">
            <MessageCircle className="w-5 h-5" />
            {chatUnread > 0 && <span data-testid="chat-fab-badge" className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-4 h-4 px-1 flex items-center justify-center">{chatUnread}</span>}
          </button>
        )}

        <nav className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-blush-soft">
          <div className="max-w-2xl mx-auto grid grid-cols-5">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={it.testid}
                  className={({ isActive }) =>
                    `relative flex flex-col items-center py-2 text-[10px] transition ${isActive ? "text-brand" : "text-cocoa-muted"}`
                  }
                >
                  <div className="relative">
                    <Icon className="w-5 h-5" />
                    {it.badge > 0 && (
                      <span className="absolute -top-1.5 -right-2 bg-brand text-white text-[9px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center">
                        {it.badge}
                      </span>
                    )}
                  </div>
                  <span className="mt-0.5">{it.label}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>
      </div>
    </CartCtx.Provider>
  );
}
